/**
 * @copyright Copyright 2016-2019 The MathWorks, Inc.
 */
define([
    "dojo/on",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/keys",
    "dojo/query",
    "dijit/a11y",
    "dijit/focus",
    "mw-log/Log",
    "mw-dialogs/ButtonEnum",
    "./stopEvent"
], function (on, dom, domAttr, domClass, keys, query, a11y, focusUtil, Log, ButtonEnum, stopEvent) {
    /**
     * @description Dialog focus handling
     * @module mw-dialogs/controller/Focus
     */
    return {

        _activeClass: "active",

        _focusedClass: "focused",

        _defaultButtonClass: "mwDefaultButton",

        /**
         * @function focus
         * @description Give focus to the specified or first focusable item in the dialog
         * when dialog first time opens. Focus on the latest item when switch back to the dialog.
         * @param {Object} dialog - The dialog instance
         * @example Focus.focus(dialog);
         * @static
         */
        focus (dialog) {
            let self = this;
            let focusWatchTimeout = 0;
            let focusWatchCounter = 0;

            dialog._firstFocusItem = null;
            dialog._lastFocusItem = null;
            this._getFocusItems(dialog);

            dialog._focusHandle = focusUtil.watch("curNode", (attr, oldNode, newNode) => {
                if (newNode === null) {
                    if (dialog._defaultButton) {
                        // Remove default button style when click outside the dialog
                        domClass.remove(dialog._defaultButton, self._defaultButtonClass);
                        // Remove button active state when click outside the dialog
                        domClass.remove(dialog._defaultButton, self._activeClass);
                    }
                    if (oldNode) {
                        domClass.remove(oldNode, self._activeClass);
                    }
                    // focused class is easy for testing
                    domClass.remove(dialog.domNode, self._focusedClass);
                } else {
                    let dijitPopup = self._isPopupDescendant(newNode, dialog._contentArea);
                    if (dijitPopup && a11y.isFocusable(newNode)) {
                        // if the newNode is a dijitPopup. e.g., item in a combobox
                        dialog._latestFocusedItem = dijitPopup;
                        domClass.add(dialog.domNode, self._focusedClass);
                    } else if (dialog._contentArea && dom.isDescendant(newNode, dialog._contentArea)
                        && a11y.isFocusable(newNode)) {
                        // if the newNode is a node in the content area
                        dialog._latestFocusedItem = newNode;
                        if (dialog._defaultButton) {
                            domClass.add(dialog._defaultButton, self._defaultButtonClass);
                        }
                        domClass.add(dialog.domNode, self._focusedClass);
                    } else if (dialog._buttonBar && dom.isDescendant(newNode, dialog._buttonBar) &&
                        a11y.isFocusable(newNode)) {
                        // if the newNode is a button in the button bar
                        dialog._latestFocusedItem = newNode;
                        if (dialog._defaultButton) {
                            domClass.remove(dialog._defaultButton, self._defaultButtonClass);
                        }
                        domClass.add(dialog.domNode, self._focusedClass);
                    } else if (dialog._closeNode && newNode === dialog._closeNode) {
                        // if the newNode is a close node
                        dialog._latestFocusedItem = dialog._closeNode;
                        domClass.add(dialog.domNode, self._focusedClass);
                    } else if (!dialog._firstFocusItem) {
                        // if there is no focusable item in the dialog
                        dialog._latestFocusedItem = newNode;
                        domClass.add(dialog.domNode, self._focusedClass);
                    } else {
                        // if newNode is null or something outside the dialog
                        domClass.remove(dialog.domNode, self._focusedClass);
                    }
                }

                // Force the focus in the modal dialog on the top when focus goes outside of the
                // dialog because of short cuts like Ctrl + Z
                if (dialog._isModal && newNode && dialog.domNode &&
                    dialog.domNode.classList.contains("mwModalDialogFg") &&
                    newNode.ownerDocument === dialog.domNode.ownerDocument) {
                    do {
                        if (newNode === dialog.domNode ||
                            domClass.contains(newNode, "dijitPopup")) {
                            return;
                        }
                        newNode = newNode.parentNode;
                    } while (newNode);

                    // To track the number of times focus is being forced in 1 second
                    focusWatchCounter++;

                    // Give up if some other widget in the window is trying to gain focus
                    // Generic fix for g2098604
                    if (focusWatchCounter > 5) {
                        return;
                    }
                    if (!focusWatchTimeout) {
                        // setTimeout returns timeout callback ID as number
                        focusWatchTimeout = setTimeout(() => {
                            focusWatchCounter = 0;
                            focusWatchTimeout = 0;
                        }, 1000);
                    }

                    this._forceFocusInModalDialog(dialog);
                }
            });

            // Focus back to the most recent focused item when click on a modal underlay
            if (dialog._underlay) {
                dialog._trackEventListener(on(dialog._underlay, 'click', () => {
                    this._forceFocusInModalDialog(dialog);
                }));
            }

            this.focusItem(dialog);

            // Focus back to the most recent focused item when click on a dialog blank area
            dialog.on('click', () => {
                this._getFocusItems(dialog);
                if (dialog._latestFocusedItem
                    && dialog._latestFocusedItem !== dialog.domNode
                    && domAttr.get(dialog._latestFocusedItem, "aria-disabled") !== 'true') {
                    focusUtil.focus(dialog._latestFocusedItem);
                } else {
                    if (dialog._firstFocusItem) {
                        focusUtil.focus(dialog._firstFocusItem);
                    } else {
                        focusUtil.focus(dialog.domNode);
                    }
                }
            });

            // Invoke keyup callback only when keydown is pressed on the dialog
            dialog._keyDownReady = false;

            dialog.on('keydown', (e) => {
                self._onKeyDown(e, dialog);
            });

            dialog.on('keyup', (e) => {
                self._onKeyUp(e, dialog);
            });
        },

        _forceFocusInModalDialog (dialog) {
            if (!dialog._firstFocusItem) {
                focusUtil.focus(dialog.domNode);
            } else {
                focusUtil.focus(dialog._latestFocusedItem);
            }
        },

        // Finds focusable items in dialog,
        // and sets dialog._firstFocusItem and dialog._lastFocusItem
        _getFocusItems (dialog) {
            dialog._firstFocusItem =
                (dialog._contentArea ? a11y.getFirstInTabbingOrder(dialog._contentArea) : undefined)
                || (dialog._buttonBar ? a11y.getFirstInTabbingOrder(dialog._buttonBar) : undefined)
                || dialog._closeNode;
            dialog._lastFocusItem =
                a11y.getLastInTabbingOrder(dialog.domNode) || dialog._firstFocusItem;
        },

        // Determine whether a node is a dijitPopup and its dijitPopupParent is the descendant
        // of the ancestor
        _isPopupDescendant (node, ancestor) {
            try {
                let dijitPopup = null;
                while (node) {
                    if (domClass.contains(node, "dijitPopup")) {
                        let popupParent = domAttr.get(node, "dijitPopupParent");
                        if (popupParent) {
                            dijitPopup = node = document.getElementById(popupParent);
                        }
                    }
                    if (node === ancestor) {
                        return dijitPopup;
                    }

                    node = node.parentNode;
                }
            } catch (e) { /* squelch */
            }

            return false;
        },

        // Give focus to the specified or default item in the dialog
        focusItem (dialog) {
            dialog.bringToTop();
            if (dialog.specifiedFocusItem) {
                let node;
                // Focus on button bar
                if (typeof dialog.specifiedFocusItem === "object") {
                    let query = `span[data-test-id=${dialog.specifiedFocusItem['data-test-id']}]`;
                    node = dialog.domNode.querySelector(query);
                    if (!node) {
                        // Forcing first focusable item to be in buttonbar to
                        // avoid focus going back to content area
                        dialog._firstFocusItem = dialog._buttonBar ?
                            a11y.getFirstInTabbingOrder(dialog._buttonBar) :
                            dialog._closeNode ? dialog._closeNode : undefined;
                    }
                } else { // If string
                    node = dom.byId(dialog.specifiedFocusItem);
                }

                if (node === null) {
                    // Couldn't find the node, focus default
                    Log.log("specifiedFocusItem couldn't be found");
                    this._focusDefaultItem(dialog);
                } else if (dom.isDescendant(node, dialog.domNode)) {
                    if (a11y.isFocusable(node)) {
                        // The specified item is focusable, focus the item
                        focusUtil.focus(node);
                    } else {
                        let focusNode = a11y.getFirstInTabbingOrder(node);
                        if (focusNode !== undefined) {
                            // There is a focusable node in the specified item, focus that node
                            focusUtil.focus(focusNode);
                        } else {
                            // There is no focusable node in the specified item, focus default
                            Log.log("specifiedFocusItem is not focusable");
                            this._focusDefaultItem(dialog);
                        }
                    }
                } else {
                    // "node" is not a valid domNode, focus default
                    Log.log("specifiedFocusItem should be an id");
                    this._focusDefaultItem(dialog);
                }
            } else {
                // No specifiedFocusItem defined, focus default
                this._focusDefaultItem(dialog);
            }
        },

        // 1. If the first focusable item is in the button bar, then give focus to the default
        // button
        // 2. If there is no focusable item in the dialog, then focus the dialog.
        // 3. Otherwise, focus the first focusable item
        _focusDefaultItem (dialog) {
            if (dialog._firstFocusItem) {
                if (dialog._buttonBar && dialog._defaultButton
                    && dom.isDescendant(dialog._firstFocusItem, dialog._buttonBar)
                    && dialog._defaultButton !== dialog._firstFocusItem
                    && domAttr.get(dialog._defaultButton, "aria-disabled") !== 'true') {
                    focusUtil.focus(dialog._defaultButton);
                } else {
                    focusUtil.focus(dialog._firstFocusItem);
                }
            } else {
                focusUtil.focus(dialog.domNode);
            }
        },

        _onKeyDown (/*Event*/ evt, dialog) {
            dialog._keyDownReady = true;
            if (evt.keyCode === keys.TAB) {
                this._getFocusItems(dialog);
                let node = evt.target;
                if (node === dialog._firstFocusItem && evt.shiftKey) {
                    // if we are shift-tabbing from first focusable item in dialog, send focus to
                    // the close node or last focusable item
                    if (dialog._closeNode) {
                        focusUtil.focus(dialog._closeNode);
                    } else {
                        focusUtil.focus(dialog._lastFocusItem);
                    }
                    stopEvent(evt);
                } else if (node === dialog._lastFocusItem && !evt.shiftKey) {
                    // if we are tabbing from last focusable item in dialog, send focus to the
                    // close node or first focusable item
                    if (dialog._closeNode) {
                        focusUtil.focus(dialog._closeNode);
                    } else {
                        focusUtil.focus(dialog._firstFocusItem);
                    }
                    stopEvent(evt);
                } else if (node === dialog._closeNode && !evt.shiftKey) {
                    // if we are tabbing from the close node in dialog, send focus to the
                    // first focusable item
                    if (dialog._closeNode !== dialog._firstFocusItem) {
                        focusUtil.focus(dialog._firstFocusItem);
                    }
                    stopEvent(evt);
                } else if (node === dialog._closeNode && evt.shiftKey) {
                    // if we are shift-tabbing from the close node in dialog, send focus to
                    // the last focusable item
                    if (dialog._closeNode !== dialog._lastFocusItem) {
                        focusUtil.focus(dialog._lastFocusItem);
                    }
                    stopEvent(evt);
                }
            } else if (evt.keyCode === keys.SPACE) {
                if (dialog._buttonBar && dom.isDescendant(focusUtil.curNode, dialog._buttonBar) &&
                    a11y.isFocusable(focusUtil.curNode)) {
                    // if current focused node is a button in the button bar, then press down the
                    // current focused button
                    stopEvent(evt);
                    domClass.add(focusUtil.curNode, "active");
                } else if (focusUtil.curNode === dialog._closeNode) {
                    // If the current focused node is close button, then press down the close button
                    stopEvent(evt);
                    focusUtil.curNode.click(evt);
                }
            } else if (evt.keyCode === keys.ENTER) {
                if (dialog._contentArea && dialog._defaultButton &&
                    dom.isDescendant(focusUtil.curNode, dialog._contentArea)
                    && a11y.isFocusable(focusUtil.curNode) && focusUtil.curNode.tagName &&
                    focusUtil.curNode.tagName.toLowerCase() === "input" &&
                    focusUtil.curNode.getAttribute("type").toLowerCase() === "text") {
                    // if current focused node is a textbox or textarea, then press down the default
                    // button
                    stopEvent(evt);
                    domClass.add(dialog._defaultButton, "active");
                } else if (dialog._buttonBar
                    && dom.isDescendant(focusUtil.curNode, dialog._buttonBar)
                    && a11y.isFocusable(focusUtil.curNode)) {
                    // if current focused node is a button in the button bar, then press down the
                    // current focused button
                    stopEvent(evt);
                    domClass.add(focusUtil.curNode, "active");
                } else if (focusUtil.curNode === dialog._closeNode) {
                    // If the current focused node is close button, then press down the close button
                    stopEvent(evt);
                    focusUtil.curNode.click(evt);
                }
            }
        },

        _onKeyUp (/*Event*/ evt, dialog) {
            if (dialog._keyDownReady) {
                if (evt.keyCode === keys.SPACE) {
                    if (dialog._buttonBar &&
                        dom.isDescendant(focusUtil.curNode, dialog._buttonBar) &&
                        a11y.isFocusable(focusUtil.curNode)) {
                        // if current focused node is a button in the button bar, then press up
                        // and trigger the current focused button
                        domClass.remove(focusUtil.curNode, "active");
                        stopEvent(evt);
                        focusUtil.curNode.click(evt);
                    }
                } else if (evt.keyCode === keys.ENTER) {
                    if (dialog._contentArea && dialog._defaultButton &&
                        dom.isDescendant(focusUtil.curNode, dialog._contentArea)
                        && a11y.isFocusable(focusUtil.curNode) && focusUtil.curNode.tagName &&
                        focusUtil.curNode.tagName.toLowerCase() === "input" &&
                        focusUtil.curNode.getAttribute("type").toLowerCase() === "text") {
                        // if current focused node is a textbox, then press up
                        // and trigger the default button
                        domClass.remove(dialog._defaultButton, "active");
                        stopEvent(evt);
                        dialog._defaultButton.click(evt);
                    } else if (dialog._buttonBar
                        && dom.isDescendant(focusUtil.curNode, dialog._buttonBar)
                        && a11y.isFocusable(focusUtil.curNode)) {
                        // if current focused node is a button in the button bar, then press up
                        // and trigger the current focused button
                        domClass.remove(focusUtil.curNode, "active");
                        stopEvent(evt);
                        focusUtil.curNode.click(evt);
                    }
                }
            }
        }
    };
});